import { debounce } from 'lodash';
import { AnyAction, Reducer } from 'redux';
import { AppStore } from '../types/common';
import { RootState } from '../types/redux';

type Slice = keyof RootState;

export enum StorageType {
  LOCAL = 'local',
  SESSION = 'session',
}

export type PersistConfig = {
  [S in StorageType]?: Slice[];
};

export const PersistReduxConfig: PersistConfig = {
  session: ['authConfig', 'authProgress'],
  local: ['device'],
};

const REHYDRATE = 'APP_REHYDRATE';

const slicesToPurge = new Set<Slice>();

export const getReduxSliceStorageKey = (slice: Slice) => `reduxPersist:login:${slice}`;

/**
 * KU
 *
 * attempt to safely access either `window.localStorage` or `window.sessionStorage`
 *
 * */
const getStorage = (storage: 'local' | 'session') => {
  try {
    return globalThis[`${storage}Storage`];
  } catch {
    return undefined;
  }
};

export const purgePersistedReduxState = () =>
  [...slicesToPurge].forEach(slice => {
    getStorage(StorageType.SESSION)?.removeItem(getReduxSliceStorageKey(slice));
    getStorage(StorageType.LOCAL)?.removeItem(getReduxSliceStorageKey(slice));
  });

export const getStateFromStorage = (slices: Slice[], storage: StorageType) => {
  return slices.reduce((acc, slice) => {
    let state: any;
    try {
      state = getStorage(storage)?.getItem(getReduxSliceStorageKey(slice));
      state = JSON.parse(state);
      return { ...acc, ...(state && { [slice]: state }) };
    } catch (error) {
      // KU – ignore errors like NS_ERROR_STORAGE_CONSTRAINT,
      // NS_ERROR_FILE_NO_DEVICE_SPACE, and NS_ERROR_FILE_NOT_FOUND
      return acc;
    }
  }, {});
};

export const getPersistedReduxState = ({
  session = [],
  local = [],
} = PersistReduxConfig): Partial<RootState> => ({
  ...getStateFromStorage(session, StorageType.SESSION),
  ...getStateFromStorage(local, StorageType.LOCAL),
});

export const persistStateToStorage = (state: RootState, slices: Slice[], storage: StorageType) => {
  try {
    slices.forEach(slice =>
      getStorage(storage)?.setItem(getReduxSliceStorageKey(slice), JSON.stringify(state[slice])),
    );
  } catch {
    // nothing we can do about browser storage errors like QuotaExceededError
  }
};

export const persistStoreToStorage = (
  store: AppStore,
  { session = [], local = [] }: PersistConfig = PersistReduxConfig,
  delay = 250,
) => {
  let prevState = {} as any;
  const onChange = () => {
    const state = store.getState();
    persistStateToStorage(
      state,
      session.filter(slice => state[slice] !== prevState[slice]),
      StorageType.SESSION,
    );
    persistStateToStorage(
      state,
      local.filter(slice => state[slice] !== prevState[slice]),
      StorageType.LOCAL,
    );
    prevState = state;
  };

  session.forEach(it => slicesToPurge.add(it));
  local.forEach(it => slicesToPurge.add(it));

  store.subscribe(debounce(onChange, delay));

  // KU – rehydrate store
  store.dispatch({
    type: REHYDRATE,
    payload: getPersistedReduxState({ session, local }),
  });
};

/**
 * KU
 *
 * creates a ["crossSliceReducer"](https://redux.js.org/usage/structuring-reducers/beyond-combinereducers#sharing-data-between-slice-reducers) that will either:
 *
 * 1. merge the current redux state with action.payload **OR**
 * 2. use the provided {@link appReducer} to handle whatever action was dispatched.
 *
 * ---
 *
 * **NOTE**: We create a "crossSliceReducer" instead of leveraging `combineReducers` because
 * the {@link REHYDRATE} reducer has to update multiple slices in the redux
 * state. `combineReducers` creates slices that correspond with each reducer.
 * But these reducers can only make changes to their "portion" of the redux
 * state.
 */
export const addRehydrateStoreReducer = <T>(appReducer: Reducer<T>): Reducer<T> => {
  return (state, action: AnyAction) => {
    if (state && action.type === REHYDRATE) {
      const persistedState = { ...action.payload } as Partial<RootState>;
      return {
        ...state,
        // KU – perform a deep-ish merge with persistedState
        ...Object.entries(persistedState).reduce(
          (acc, [key, value]) => ({ ...acc, [key]: { ...state[key as keyof T], ...value } }),
          {},
        ),
      };
    }
    return appReducer(state, action);
  };
};
